import React, { useState, useEffect } from "react";
import "../../assets/css/members.css";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import CustomDropdown from "../../Components/Common/CustomDropdown";
import formatDate from "../../Components/DateFormat";
import VerticalSpace from "../../Components/VerticalSpace";
import TextField from "../../Components/TextFields/TextField";
import "../../stylesheets/indexpage.css";
import PaginationComponent from "../../Components/Common/PaginationComponent";
import { baseURL, getRequest, postRequest } from "../../api/apiClient";
import Toast from "../../Components/Common/Toast";
import DeleteConfirmation from "../../Components/Common/DeleteConfirmation";
import { setDropDown } from "../../redux/lessonsSlice";
import { setParticles } from "../../redux/particlesReducerSlice";
import { useDispatch } from "react-redux";
import Loader from "../../utils/Loading";
import gsap from 'gsap';
import '../../stylesheets/common.css';
import ShimmerEffect from "../../utils/ShimmerEffect";
import { useDebounce } from "../../hooks/useDebounce";
import { useResponsiveFlex } from "../../utils/useResponsiveFlex";
import CloneLevelPopup from "../../Components/Common/CloneLevelPopup";
import copyIcon from "../../assets/copyIcon.svg";

const InnerRowContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 1100px;
`;

const ScrollableRowContainer = styled(RowContainer)`
  @media (max-width: 1150px) {
    width: 100%;
    overflow-x: auto;
  }
`;

interface TextFieldsList {
  id: number;
  question: string;
  themeName: string;
  themeIsDeleted: boolean;
  lessonLevel: string;
  points: string;
  updatedAt: string;
  active: boolean;
  include?: boolean;
}

const TextFieldsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [textFields, setTextFields] = useState<TextFieldsList[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(searchParams.get("filter") || null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(searchParams.get("level") || null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTextFields, setSelectedTextFields] = useState<number[]>([]);
  const [selectedTextFieldId, setSelectedTextFieldId] = useState<number | null>(
    null
  );
  const [showCloneLevelPopup, setShowCloneLevelPopup] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0"));
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit") || "5"));
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const flex = useResponsiveFlex();

  const getFiltersFromItem = (item: string | null): { level?: string; updatedAt?: number; active?: string } => {
    switch (item) {
      case "Latest update": return { updatedAt: -1 };
      case "Active": return { active: "true" };
      case "InActive": return { active: "false" };
      default: return {};
    }
  };

  const buildParams = (p: number, l: number, s: string, f: string | null, lv: string | null): Record<string, string> => {
    const params: Record<string, string> = { page: p.toString(), limit: l.toString() };
    if (s) params.search = s;
    if (f && f !== "All") params.filter = f;
    if (lv && lv !== "All Levels") params.level = lv;
    return params;
  };

  const [filters, setFilters] = useState<{
    level?: string;
    updatedAt?: number;
    active?: string;
  }>(() => {
    const init = getFiltersFromItem(searchParams.get("filter"));
    const urlLevel = searchParams.get("level");
    if (urlLevel && urlLevel !== "All Levels") init.level = urlLevel;
    return init;
  });

  const handleSelect = (item: string) => {
    setSelectedItem(item);
  };

  const handleLevelSelect = (item: string) => {
    setSelectedLevel(item);
  };

  const dropdownItems: string[] = [
    "All",
    "Active",
    "InActive",
    "Latest update",
  ];

  const levelDropdownItems: string[] = [
    "All Levels",
    "A1",
    "A2",
    "B1",
    "B2",
  ];

  const debouncedSearch = useDebounce(searchQuery, 500);

  // Sync state when browser Back/Forward changes the URL
  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") || "0");
    const urlLimit = parseInt(searchParams.get("limit") || "5");
    const urlSearch = searchParams.get("search") || "";
    const urlFilter = searchParams.get("filter") || null;
    const urlLevel = searchParams.get("level") || null;
    setPage(urlPage);
    setLimit(urlLimit);
    setSearchQuery(urlSearch);
    setSelectedItem(urlFilter);
    setSelectedLevel(urlLevel);
    const synced = getFiltersFromItem(urlFilter);
    if (urlLevel && urlLevel !== "All Levels") synced.level = urlLevel;
    setFilters(synced);
  }, [searchParams]);

  useEffect(() => {
    const updatedFilters = {
      ...filters,
      search: debouncedSearch,
    };
    fetchTextFields(page + 1, limit, updatedFilters);
  }, [debouncedSearch, page, limit, filters]);

  //fetch textfields list
  const fetchTextFields = async (
    page: number,
    perPage: number,
    filters: {
      level?: string;
      updatedAt?: number;
      active?: string;
      search?: string;
    }
  ) => {
    if (!filters) {
      setLoading(true);
    } else {
      setFilterLoading(true);
    }

    try {
      const url = `${baseURL}/textfield-challenge/get-all-textfield-challenge`;
      const body = {
        page,
        limit: perPage,
        ...filters,
      };
      const response = await postRequest(url, body);
      setLoading(false);
      setFilterLoading(false);
      if (response?.data) {
        setTextFields(response.data.data.result);
        setCount(response.data.data.totalChallengesCount);
        setPage(response.data.data.page - 1);
        setHasNextPage(response.data.data.hasNextPage);
        setHasPreviousPage(response.data.data.hasPreviousPage);
      }

      const fetchLessons = await getRequest(
        `${baseURL}/notification-challenge/get-lesson-dropdown`
      );

      const fetchParticles = await getRequest(
        `${baseURL}/particle/get-dropdown`
      );
      dispatch(setDropDown(fetchLessons?.data?.data || []));
      dispatch(setParticles(fetchParticles?.data?.data || []));
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(0);
    setSearchParams(buildParams(0, limit, query, selectedItem, selectedLevel));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    setSearchParams(buildParams(newPage, limit, searchQuery, selectedItem, selectedLevel));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    setSearchParams(buildParams(0, newLimit, searchQuery, selectedItem, selectedLevel));
  };

  const handleApplyFilters = () => {
    const newFilters = getFiltersFromItem(selectedItem);
    if (selectedLevel && selectedLevel !== "All Levels") {
      newFilters.level = selectedLevel;
    }
    setFilters(newFilters);
    setPage(0);
    setSearchParams(buildParams(0, limit, searchQuery, selectedItem, selectedLevel));
  };

  const handleEditTextFields = (id: number) => {
    navigate(`/textfields/edit-textfields/${id}`, {
      state: { listPage: page, listLimit: limit, listSearch: searchQuery, listFilter: selectedItem, listLevel: selectedLevel },
    });
  };

  const handleCopyTextField = async (id: number) => {
    // Open the level selection popup instead of directly cloning
    setSelectedTextFieldId(id);
    setShowCloneLevelPopup(true);
  };

  /** Bulk copy all selected text fields — clones each one server-side */
  const handleCopySelectedTextFields = async () => {
    if (selectedTextFields.length === 0) return;
    // Open level popup for bulk copy
    setShowCloneLevelPopup(true);
  };

  const handleCloneWithLevel = async (level: string) => {
    try {
      if (selectedTextFieldId !== null) {
        // Single clone
        const response = await postRequest(
          `${baseURL}/textfield-challenge/clone-textfield-challenge`,
          { id: selectedTextFieldId, level }
        );
        if (response?.data?.success) {
          Toast(`Text field cloned to ${level} successfully`, "success");
          const updatedFilters = { ...filters, search: debouncedSearch };
          fetchTextFields(page + 1, limit, updatedFilters);
        }
        setShowCloneLevelPopup(false);
        setSelectedTextFieldId(null);
      } else if (selectedTextFields.length > 0) {
        // Bulk clone
        let successCount = 0;
        let failedCount = 0;
        
        for (const id of selectedTextFields) {
          const response = await postRequest(
            `${baseURL}/textfield-challenge/clone-textfield-challenge`,
            { id, level }
          );
          if (response?.data?.success) {
            successCount++;
          } else {
            failedCount++;
          }
        }
        
        if (successCount > 0) {
          Toast(`${successCount} text field(s) cloned to ${level} successfully`, "success");
        }
        if (failedCount > 0) {
          Toast(`${failedCount} text field(s) failed to clone`, "error");
        }
        
        setSelectedTextFields([]);
        const updatedFilters = { ...filters, search: debouncedSearch };
        fetchTextFields(page + 1, limit, updatedFilters);
        setShowCloneLevelPopup(false);
        setSelectedTextFieldId(null);
      }
    } catch (error: any) {
      console.error("Error cloning text field:", error);
      // Errors are already handled and toasted by apiClient
      setShowCloneLevelPopup(false);
      setSelectedTextFieldId(null);
    }
  };

  const handleCreateTextFields = () => {
    navigate("/textfields/create-textfields");
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const reqData = {
      modelName: "textfieldsChallenge",
      id: id,
      isActive: !active,
    };
    await postRequest(`${baseURL}/change-status`, reqData);
    // await fetchTextFields(page + 1, limit, filters);
  };

  const handleIncludeChange = async (id: number, include: boolean) => {
    const reqData = {
      type: "textfields",
      id: id,
      include: include,
    };
    await postRequest(`${baseURL}/challenge/include/change`, reqData);
  };

  const onCheckboxChange = (id: number) => {
    if (selectedTextFields.includes(id)) {
      setSelectedTextFields(
        selectedTextFields.filter((selectedId) => selectedId !== id)
      );
    } else {
      setSelectedTextFields([...selectedTextFields, id]);
    }
  };

  const deleteSelectedTextFields = () => {
    if (selectedTextFields.length > 0) {
      setShowDeleteDialog(true);
    } else {
      Toast("Please select at least one challenge to delete.", "error");
    }
  };

  const deleteTextFields = async (textfieldIds: number | number[]) => {
    try {
      const payload = {
        id: Array.isArray(textfieldIds) ? textfieldIds : textfieldIds,
      };
      const response = await postRequest(
        `${baseURL}/textfield-challenge/delete-textfield-challenge-by-id`,
        payload
      );

      if (response?.status === 200) {
        Toast("textfields deleted successfully", "success");
        setTextFields((prevData) =>
          prevData.filter((textfields) =>
            Array.isArray(textfieldIds)
              ? !textfieldIds.includes(textfields.id)
              : textfields.id !== textfieldIds
          )
        );
      } else {
        Toast("Failed to delete challenge", "error");
      }
    } catch (error) {
      console.error("Error deleting challenge:", error);
    }
  };

  const handleDelete = async () => {
    if (selectedTextFieldId !== null) {
      await deleteTextFields(selectedTextFieldId);
    } else {
      await deleteTextFields(selectedTextFields);
    }
    // await fetchTextFields(page + 1, limit, filters);
    setSelectedTextFields([]);
    setSelectedTextFieldId(null);
    setShowDeleteDialog(false);
  };

  const toggleDeleteDialog = () => {
    setShowDeleteDialog(!showDeleteDialog);
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.2
    });


  }, [loading]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="container-fluid">
            <div className="p-3">
              <div className="row mb-3 ">
                <div className="col">
                  <h2 style={{ fontWeight: "bold" }}>Challenge: text fields</h2>
                </div>
              </div>

              {/* Filter Label */}
              <div className="row mb-3">
                <div className="col">
                  <label htmlFor="filter" className="form-label">
                    Filter
                  </label>
                </div>
              </div>

              {/* Buttons Row */}
              <div className="row mb-3 all-button-responsive">
                <div className="col-3 search-input-div">
                  <input
                    type="search"
                    className="form-control rounded"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    aria-label="Search"
                    aria-describedby="search-addon"
                    style={{
                      height: "100%",
                      boxShadow: "none",
                    }}
                  />
                </div>
                <div className="col d-flex responsive">
                  <CustomDropdown
                    items={dropdownItems}
                    onSelect={handleSelect}
                    selectedItem={selectedItem || ""}
                    height="50px"
                  />
                  <div className="ms-3">
                    <CustomDropdown
                      items={levelDropdownItems}
                      onSelect={handleLevelSelect}
                      selectedItem={selectedLevel || ""}
                      height="50px"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary ms-3 user"
                    style={{
                      backgroundColor: "#100F15",
                      borderRadius: "10px",
                      color: "#FFFFFF",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 50px",
                    }}
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </button>
                </div>
                <div className="col d-flex justify-content-end responsive">
                  {/* <button
                    type="button"
                    className="btn btn-outline-secondary me-2 text-center"
                    style={{
                      color: "#100F15",
                      background: "none",
                      border: "1px solid #100F15",
                      borderRadius: "10px",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={deleteSelectedTextFields}
                  >
                    Delete all
                  </button> */}
                  <button
                    type="button"
                    className="btn btn-success user"
                    style={{
                      backgroundColor: "#FF9E00",
                      borderRadius: "10px",
                      color: "#100F15",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={handleCreateTextFields}
                  >
                    Add New
                  </button>
                </div>
              </div>

              <ScrollableRowContainer>
                <InnerRowContainer>
                  <Container width="100%">
                    <RowContainer
                      alignItem="center"
                      padding="8px 12px 8px 12px"
                      gap="8px"
                      justifyContent="space-between"
                      className="row-container"
                    >
                      {/* Select-all checkbox for current page */}
                      <RowContainer flex="1.4" alignItem="center">
                        <input
                          type="checkbox"
                          title="Select all on this page"
                          checked={
                            textFields.length > 0 &&
                            textFields.every((t) =>
                              selectedTextFields.includes(t.id)
                            )
                          }
                          ref={(el) => {
                            if (el) {
                              el.indeterminate =
                                selectedTextFields.length > 0 &&
                                !textFields.every((t) =>
                                  selectedTextFields.includes(t.id)
                                );
                            }
                          }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const currentPageIds = textFields.map((t) => t.id);
                              setSelectedTextFields((prev) => [
                                ...prev.filter((id) => !currentPageIds.includes(id)),
                                ...currentPageIds,
                              ]);
                            } else {
                              const currentPageIds = textFields.map((t) => t.id);
                              setSelectedTextFields((prev) =>
                                prev.filter((id) => !currentPageIds.includes(id))
                              );
                            }
                          }}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: "#7542E4",
                            cursor: "pointer",
                          }}
                        />
                      </RowContainer>
                      <RowContainer flex={flex.name}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Name
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.theme}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Theme
                        </Label>
                      </RowContainer>
                      <RowContainer flex="3.1">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Level
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.point}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Point
                        </Label>
                      </RowContainer>
                      <RowContainer flex="5">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Latest updated
                        </Label>
                      </RowContainer>
                      <RowContainer flex="2.3" alignItem="center">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Include
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.action} justifyContent="space-between" alignItem="center">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Action
                        </Label>
                        {/* Bulk Copy toolbar — visible only when text fields are selected */}
                        {selectedTextFields.length > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button
                              type="button"
                              className="btn user"
                              title={`Copy ${selectedTextFields.length} selected text field(s)`}
                              style={{
                                backgroundColor: "#DAD7FE",
                                color: "#000",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px 12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 5,
                                whiteSpace: "nowrap",
                              }}
                              onClick={handleCopySelectedTextFields}
                            >
                              <img
                                src={copyIcon}
                                alt="copy"
                                style={{ width: 16, height: 16 }}
                              />
                              Copy ({selectedTextFields.length})
                            </button>
                            <button
                              type="button"
                              className="btn user"
                              title="Clear selection"
                              style={{
                                background: "none",
                                border: "1px solid #aaa",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px 10px",
                                color: "#555",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => setSelectedTextFields([])}
                            >
                              ✕ Clear
                            </button>
                          </div>
                        )}
                      </RowContainer>
                    </RowContainer>
                    {filterLoading ? (
                      <>
                        <RowContainer
                          borderRadius="8px"
                          bgColor="#ffffff"
                          padding="10px 0px"
                        >
                          {Array.from({ length: 6 }, (_, index) => {
                            return (
                              <RowContainer
                                key={index}
                                width={
                                  index === 0 || index === 5 ? "10%" : "20%"
                                }
                                justifyContent="center"
                              >
                                <ShimmerEffect />
                              </RowContainer>
                            );
                          })}
                        </RowContainer>
                      </>
                    ) : (
                      <>
                        {textFields && textFields.length > 0 ? (
                          <ColumnContainer>
                            {textFields.map((textField) => (
                              <TextField
                                key={textField.id}
                                id={textField.id}
                                name={textField.question}
                                theme={textField.themeName}
                                themeIsDeleted={textField.themeIsDeleted}
                                lessonLevel={textField.lessonLevel}
                                point={`${textField.points} pts`}
                                created={formatDate(textField.updatedAt)}
                                onEdit={handleEditTextFields}
                                onDelete={() => {
                                  setSelectedTextFieldId(textField.id);
                                  toggleDeleteDialog();
                                }}
                                isChecked={selectedTextFields.includes(
                                  textField.id
                                )}
                                onCheckboxChange={onCheckboxChange}
                                status={textField.active}
                                onStatusChange={() =>
                                  handleStatusChange(
                                    textField.id,
                                    textField.active
                                  )
                                }
                                include={textField.include ?? textField.active}
                                onIncludeChange={() =>
                                  handleIncludeChange(
                                    textField.id,
                                    !(textField.include ?? textField.active)
                                  )
                                }
                                onCopy={handleCopyTextField}
                              />
                            ))}
                          </ColumnContainer>
                        ) : (
                          <>
                            <VerticalSpace height={30} />
                            <Label
                              alignItems="center"
                              display="flex"
                              color="rgba(164, 163, 167, 1)"
                              justifyContent="center"
                            >
                              No results found.
                            </Label>
                          </>
                        )}
                      </>
                    )}
                  </Container>
                </InnerRowContainer>
              </ScrollableRowContainer>
              <VerticalSpace height={10} />
              <RowContainer justifyContent="flex-end" className="user-data">
                <PaginationComponent
                  page={page}
                  rowsPerPage={limit}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={count}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                />
              </RowContainer>
            </div>
            {showDeleteDialog && (
              <DeleteConfirmation
                showDeletePopup={true}
                setShowDeletePopup={setShowDeleteDialog}
                message={`Are you sure you want to delete ${selectedTextFields.length === 0
                  ? "this textfield?"
                  : selectedTextFields.length === 1
                    ? "this textfields?"
                    : "these textfields?"
                  }`}
                deleteButtonLabel="Delete"
                onDelete={handleDelete}
              />
            )}
            <CloneLevelPopup
              show={showCloneLevelPopup}
              onConfirm={handleCloneWithLevel}
              onCancel={() => {
                setShowCloneLevelPopup(false);
                setSelectedTextFieldId(null);
              }}
              isBulk={selectedTextFields.length > 0}
              count={selectedTextFields.length || 1}
            />
            <VerticalSpace height={100} />
          </div>
        </>
      )}
    </>
  );
};

export default TextFieldsList;
